/**
 * Rating Formula Tab Component
 * Handles supplier rating formula weights configuration
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Scale, Info } from 'lucide-react';
import { PerformanceFormData } from './types';
import { useQualityMeasureLabel, useQualityMeasureLongLabel } from '../../../lib/qualityLabels';

interface RatingFormulaTabProps {
  formData: PerformanceFormData;
  onWeightChange: (field: 'otif_weight' | 'ppm_weight', value: string) => void;
}

export function RatingFormulaTab({
  formData,
  onWeightChange,
}: RatingFormulaTabProps) {
  const qualityLabel = useQualityMeasureLabel();
  const qualityLongLabel = useQualityMeasureLongLabel();
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-primary" />
            <CardTitle>Supplier Rating Formula</CardTitle>
          </div>
          <CardDescription>
            Configure the weights for OTIF and {qualityLabel} in the overall supplier rating calculation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Weight Sliders */}
          <div className="space-y-4">
            <h3 className="font-medium">Rating Component Weights</h3>
            <p className="text-sm text-muted-foreground">
              The weights must total 100%. Adjusting one value will automatically adjust the other.
            </p>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="otif_weight" className="flex items-center justify-between">
                  <span>OTIF Weight</span>
                  <Badge variant="outline">{formData.otif_weight}%</Badge>
                </Label>
                <Input
                  id="otif_weight"
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={parseFloat(formData.otif_weight)}
                  onChange={(e) => onWeightChange('otif_weight', e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Delivery performance (On-Time In-Full)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ppm_weight" className="flex items-center justify-between">
                  <span>{qualityLabel} Weight</span>
                  <Badge variant="outline">{formData.ppm_weight}%</Badge>
                </Label>
                <Input
                  id="ppm_weight"
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={parseFloat(formData.ppm_weight)}
                  onChange={(e) => onWeightChange('ppm_weight', e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Quality performance ({qualityLongLabel} defects)
                </p>
              </div>
            </div>
          </div>

          {/* Weight Visualization */}
          <div className="space-y-2">
            <h3 className="font-medium">Weight Distribution</h3>
            <div className="h-8 flex rounded-lg overflow-hidden">
              <div
                className="bg-blue-500 flex items-center justify-center text-white text-sm font-medium"
                style={{ width: `${formData.otif_weight}%` }}
              >
                {parseFloat(formData.otif_weight) >= 20 && `OTIF ${formData.otif_weight}%`}
              </div>
              <div
                className="bg-orange-500 flex items-center justify-center text-white text-sm font-medium"
                style={{ width: `${formData.ppm_weight}%` }}
              >
                {parseFloat(formData.ppm_weight) >= 20 && `${qualityLabel} ${formData.ppm_weight}%`}
              </div>
            </div>
          </div>

          {/* Rating Formula Info */}
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <span className="font-medium">Supplier Rating Calculation</span>
            </div>
            <code className="block text-sm p-2 bg-background rounded border">
              Rating Score = (OTIF Score x {parseFloat(formData.otif_weight) / 100}) + ({qualityLabel} Score x {parseFloat(formData.ppm_weight) / 100})
            </code>
            <p className="text-sm text-muted-foreground">
              The OTIF Score and {qualityLabel} Score are determined by the score mappings configured in their respective tabs.
              The final rating score is then mapped to a letter grade (A-F) based on the grade thresholds.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
